import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, 
  AlertTriangle, 
  Upload, 
  TrendingUp, 
  Shield,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  ShoppingCart,
  ShoppingBag,
  Scale,
  GitCompare
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_files: 0,
    total_anomalies: 0,
    recent_uploads: 0,
    user_role: '',
    user_name: ''
  });
  const [recentFiles, setRecentFiles] = useState([]);
  const [recentAnomalies, setRecentAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, filesResponse, anomaliesResponse] = await Promise.all([
        axios.get(`${API}/audit/dashboard-stats`),
        axios.get(`${API}/files/`),
        axios.get(`${API}/ml/results`)
      ]);

      setStats(statsResponse.data);
      setRecentFiles(filesResponse.data.slice(0, 5));
      setRecentAnomalies(anomaliesResponse.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, description, color = "indigo", trend }) => (
    <Card className="hover:shadow-md transition-all border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</CardTitle>
        <div className={`p-2 rounded-md bg-${color}-50`}>
          <Icon className={`h-4 w-4 text-${color}-600`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold tracking-tight text-slate-900">{value}</div>
        {description && (
          <p className="text-xs text-slate-500 mt-1 font-medium">{description}</p>
        )}
        {trend && (
          <div className="flex items-center mt-3 text-xs">
            <div className={`flex items-center px-1.5 py-0.5 rounded-full ${trend > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              <TrendingUp className={`h-3 w-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span className="font-bold">{Math.abs(trend)}%</span>
            </div>
            <span className="text-slate-400 ml-2">vs last week</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const getAnomalySeverityBadge = (anomalies) => {
    const highSeverity = anomalies.filter(a => a.severity === 'high').length;
    if (highSeverity > 0) {
      return <Badge variant="destructive" className="text-xs">High Risk</Badge>;
    }
    const mediumSeverity = anomalies.filter(a => a.severity === 'medium').length;
    if (mediumSeverity > 0) {
      return <Badge variant="secondary" className="text-xs">Medium Risk</Badge>;
    }
    return <Badge variant="outline" className="text-xs">Low Risk</Badge>;
  };

  const formatAnalysisType = (type) => {
    const typeMap = {
      'purchase_reconciliation': 'Purchase Reconciliation',
      'sales_reconciliation': 'Sales Reconciliation',
      'exceptional_balance': 'Exceptional Balance',
      'opening_balance_scrutiny': 'Opening Balance Scrutiny'
    };
    return typeMap[type] || type;
  };

  const getAnalysisTypeColor = (type) => {
    const colorMap = {
      'purchase_reconciliation': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'sales_reconciliation': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'exceptional_balance': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'opening_balance_scrutiny': 'bg-indigo-50 text-indigo-700 border-indigo-200'
    };
    return colorMap[type] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Shield className="h-32 w-32 text-indigo-600" />
        </div>
        <div className="relative z-10">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">
              Welcome back, {user?.full_name || 'Auditor'}
            </h1>
            <p className="text-slate-500 max-w-2xl mb-6 text-lg">
              System pulse is normal. You have {stats.total_anomalies} pending anomalies that require your attention.
            </p>
            <div className="flex items-center gap-3">
              <Link to="/upload">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm px-6">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Data
                </Button>
              </Link>
              <Link to="/results">
                <Button variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50 px-6">
                  Security Reports
                </Button>
              </Link>
            </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Files Analyzed"
          value={stats.total_files}
          icon={FileText}
          description="Ledger files processed"
          color="blue"
          trend={stats.trends?.files}
        />
        <StatCard
          title="Anomalies Detected"
          value={stats.total_anomalies}
          icon={AlertTriangle}
          description="Potential issues found"
          color="orange"
          trend={stats.trends?.anomalies}
        />
        <StatCard
          title="Recent Uploads"
          value={stats.recent_uploads}
          icon={Upload}
          description="Files uploaded this week"
          color="green"
          trend={stats.trends?.recent}
        />
        <StatCard
          title="System Health"
          value={stats.system_health || "99.9%"}
          icon={Shield}
          description="Platform uptime"
          color="green"
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Files */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Recent Files
            </CardTitle>
            <CardDescription>
              Latest uploaded financial ledgers
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentFiles.length > 0 ? (
              <div className="space-y-1 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                {recentFiles.map((file) => (
                    <div key={file.id} className="group flex items-center justify-between p-3 border-b border-slate-50 hover:bg-slate-50/50 transition-all cursor-pointer">
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center gap-3 mb-0.5">
                          <p className="font-bold text-sm truncate text-slate-800 group-hover:text-indigo-600 transition-colors">{file.original_filename}</p>
                          {file.analysis_type && (
                            <Badge 
                              className={`text-[9px] px-1.5 py-0 h-4 border uppercase font-black tracking-widest ${getAnalysisTypeColor(file.analysis_type)}`}
                            >
                              {formatAnalysisType(file.analysis_type).split(' ').map(w => w[0]).join('')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center text-[11px] text-slate-400 font-medium">
                           <Clock className="h-3 w-3 mr-1" />
                           {new Date(file.upload_date + (file.upload_date.endsWith('Z') ? '' : 'Z')).toLocaleString('en-IN', { 
                             day: 'numeric', 
                             month: 'short', 
                             hour: '2-digit', 
                             minute: '2-digit'
                           })}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={file.status === 'processed' ? 'success' : 'secondary'} 
                               className={`text-[10px] uppercase font-bold ${file.status === 'processed' ? '' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                          {file.status}
                        </Badge>
                      </div>
                    </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No files uploaded yet</p>
                <Link to="/upload">
                  <Button variant="outline" size="sm" className="mt-2">
                    Upload First File
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Anomalies */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Recent Anomalies
            </CardTitle>
            <CardDescription>
              Latest detection results
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentAnomalies.length > 0 ? (
              <div className="space-y-1 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                {recentAnomalies.map((result) => (
                  <div key={result.id} className="group p-3 border-b border-slate-50 hover:bg-slate-50/50 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1">
                        <p className="font-bold text-sm capitalize text-slate-800 group-hover:text-indigo-600 transition-colors">
                          {result.anomaly_type.replace('_', ' ')}
                        </p>
                        <div className="flex items-center text-[11px] text-slate-400 font-medium">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(result.created_at).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      {getAnomalySeverityBadge(result.detected_anomalies)}
                    </div>
                    <p className="text-xs text-slate-500 mb-3 line-clamp-1 italic">"{result.summary}"</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px] font-black bg-white border-slate-200">
                          {result.detected_anomalies.length} ISSUES
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                         <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Reliability</span>
                         <Progress 
                           value={result.confidence_score * 100} 
                           className="w-16 h-1 bg-slate-100" 
                         />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-3" />
                <p className="text-gray-500">No anomalies detected yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Upload files to start detecting anomalies
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Financial Analysis Tools */}
      <div className="mt-8 pt-8 border-t border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Analysis Workbench</h2>
            <p className="text-sm text-slate-500">Professional-grade financial reconciliation and validation tools</p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Link to="/purchase-reconciliation">
            <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden border-slate-200">
              <div className="h-1 w-full bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 transition-colors">
                    <ShoppingCart className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-200">GSTR-2B</Badge>
                </div>
                <CardTitle className="text-base font-bold text-slate-900">Purchase Reconciliation</CardTitle>
                <CardDescription className="text-xs min-h-[40px] mt-2">
                  High-precision matching between Purchase Register and GSTR-2B data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all font-bold text-[11px] uppercase tracking-wider">
                  Launch Tool
                </Button>
              </CardContent>
            </Card>
          </Link>

          <Link to="/sales-reconciliation">
            <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden border-slate-200">
              <div className="h-1 w-full bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 transition-colors">
                    <ShoppingBag className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-200">GSTR-1</Badge>
                </div>
                <CardTitle className="text-base font-bold text-slate-900">Sales Reconciliation</CardTitle>
                <CardDescription className="text-xs min-h-[40px] mt-2">
                  Audit sales registers against official GSTR-1 filings for discrepancies.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all font-bold text-[11px] uppercase tracking-wider">
                  Launch Tool
                </Button>
              </CardContent>
            </Card>
          </Link>

          <Link to="/exceptional-balance">
            <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden border-slate-200">
              <div className="h-1 w-full bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 transition-colors">
                    <Scale className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-200">RULES</Badge>
                </div>
                <CardTitle className="text-base font-bold text-slate-900">Exceptional Balance</CardTitle>
                <CardDescription className="text-xs min-h-[40px] mt-2">
                  Validate trial balances against Indian accounting standards and golden rules.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all font-bold text-[11px] uppercase tracking-wider">
                  Launch Tool
                </Button>
              </CardContent>
            </Card>
          </Link>

          <Link to="/opening-scrutiny">
            <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden border-slate-200">
              <div className="h-1 w-full bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 transition-colors">
                    <GitCompare className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-200">YEAR-ON-YEAR</Badge>
                </div>
                <CardTitle className="text-base font-bold text-slate-900">Opening Scrutiny</CardTitle>
                <CardDescription className="text-xs min-h-[40px] mt-2">
                   Analyze year-over-year opening balance consistency for multi-year audits.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all font-bold text-[11px] uppercase tracking-wider">
                  Launch Tool
                </Button>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      {user?.role === 'admin' && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Administrative tasks and shortcuts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <Link to="/users">
                <Button variant="outline" className="w-full justify-start h-auto p-4">
                  <Users className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Manage Users</div>
                    <div className="text-sm text-gray-500">Add or edit user accounts</div>
                  </div>
                </Button>
              </Link>
              
              <Link to="/audit">
                <Button variant="outline" className="w-full justify-start h-auto p-4">
                  <Clock className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Audit Logs</div>
                    <div className="text-sm text-gray-500">View system activity</div>
                  </div>
                </Button>
              </Link>
              
              <Button variant="outline" className="w-full justify-start h-auto p-4" onClick={() => toast.info('Coming soon!')}>
                <AlertCircle className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-medium">System Reports</div>
                  <div className="text-sm text-gray-500">Generate compliance reports</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;